import { useRef, useState } from 'react';
import { collectFilesFromDataTransfer, filterAcceptedFiles } from '../utils/easyModeFileUtils';
import useEasyModeCloudSources from './useEasyModeCloudSources';

const useEasyModeDropzone = ({ existingFileKeys, onFilesAccepted }) => {
  const [isDragActive, setIsDragActive] = useState(false);
  const [rejections, setRejections] = useState([]);
  // dragenter and dragleave fire for every child element, so a boolean would flicker
  const dragDepthRef = useRef(0);
  const fileInputRef = useRef(null);
  // webkitdirectory cannot be toggled on a live input, so the folder picker needs its own
  const folderInputRef = useRef(null);

  const acceptFiles = ({ files }) => {
    const { acceptedFiles, rejections: nextRejections } = filterAcceptedFiles({
      files,
      existingFileKeys
    });

    setRejections(nextRejections);

    if (acceptedFiles.length > 0) {
      onFilesAccepted({ files: acceptedFiles });
    }
  };

  // owned here rather than by the caller so a cloud import cannot reach the case without passing
  // through the same type, size and count checks a drop does
  const cloudSources = useEasyModeCloudSources({ onFilesReady: acceptFiles });

  const endDrag = () => {
    dragDepthRef.current = 0;
    setIsDragActive(false);
  };

  const handleDragEnter = (event) => {
    event.preventDefault();
    dragDepthRef.current += 1;
    setIsDragActive(true);
  };

  // without preventDefault on dragover the browser navigates to the dropped file
  const handleDragOver = (event) => event.preventDefault();

  const handleDragLeave = (event) => {
    event.preventDefault();
    dragDepthRef.current -= 1;

    if (dragDepthRef.current <= 0) {
      endDrag();
    }
  };

  const handleDrop = async (event) => {
    event.preventDefault();
    endDrag();
    const files = await collectFilesFromDataTransfer({ dataTransfer: event.dataTransfer });
    acceptFiles({ files });
  };

  const handleFilesSelected = ({ file }) => acceptFiles({ files: file });

  const openFilePicker = () => fileInputRef.current?.click();

  const openFolderPicker = () => folderInputRef.current?.click();

  const dismissRejections = () => setRejections([]);

  return {
    isDragActive,
    rejections,
    fileInputRef,
    folderInputRef,
    cloudSources,
    openFilePicker,
    openFolderPicker,
    handleFilesSelected,
    dismissRejections,
    dragHandlers: {
      onDragEnter: handleDragEnter,
      onDragOver: handleDragOver,
      onDragLeave: handleDragLeave,
      onDrop: handleDrop
    }
  };
};

export default useEasyModeDropzone;
